export const REPORT_FORMATS = [
  { code: 'CLASSIC', name: 'Classic Clinical', description: 'Formal laboratory report: logo and address header, two-column patient block, department-wise results table with H/L flags.' },
  { code: 'MODERN', name: 'Modern Wellness', description: 'Colourful health-report look: patient card, parameters with a range bar and status colour, easy for patients to read.' },
  { code: 'COMPACT', name: 'Compact Corporate', description: 'Dense, space-saving layout for long reports: small type, tight rows, summary of abnormal values on top.' },
  { code: 'LETTERHEAD', name: 'Pre-printed Letterhead', description: "No header or footer artwork: leaves blank space at the top and bottom for the centre's pre-printed letterhead paper." },
];

export const DEFAULT_REPORT_FORMAT = 'CLASSIC';
export const isReportFormat = (code) => REPORT_FORMATS.some((f) => f.code === code);

export const formatRefRange = (obs) => {
  if (obs.displayReading) return obs.displayReading;
  if (obs.minReading && obs.maxReading) return `${obs.minReading} - ${obs.maxReading}`;
  if (obs.minReading) return `> ${obs.minReading}`;
  if (obs.maxReading) return `< ${obs.maxReading}`;
  return '—';
};

export const getObsValue = (obs) => {
  const v = obs.currentValue !== undefined && obs.currentValue !== null && obs.currentValue !== '' ? obs.currentValue : obs.value;
  return v !== undefined && v !== null && v !== '' ? String(v) : '—';
};

export const getObsName = (obs) => obs.name || obs.observationName || '';

/** 'normal' | 'high' | 'low' | 'abnormal' | 'critical-high' | 'critical-low' */
export const flagKind = (flag) => {
  switch (flag) {
    case 'HH': return 'critical-high';
    case 'LL': return 'critical-low';
    case 'H': return 'high';
    case 'L': return 'low';
    case 'A': return 'abnormal';
    default: return 'normal';
  }
};

export const isFlagged = (flag) => flagKind(flag) !== 'normal';
export const isCriticalFlag = (flag) => flag === 'HH' || flag === 'LL';

export const FLAG_LABEL = {
  normal: 'Normal', high: 'High', low: 'Low', abnormal: 'Abnormal',
  'critical-high': 'Critical High', 'critical-low': 'Critical Low',
};

/** Short marker printed next to the value in the classic and compact layouts. */
export const FLAG_MARK = { normal: '', high: 'H', low: 'L', abnormal: 'A', 'critical-high': 'HH', 'critical-low': 'LL' };

const num = (x) => {
  if (x === undefined || x === null || x === '') return null;
  const n = Number(String(x).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

/**
 * Position (0-100) of a numeric value on a bar that spans the reference interval with some room on both sides,
 * plus where the normal band starts and ends. Returns null when the value or the interval is not numeric.
 */
export const rangeBar = (obs) => {
  const v = num(obs.currentValue ?? obs.value);
  const lo = num(obs.minReading);
  const hi = num(obs.maxReading);
  if (v === null || lo === null || hi === null || hi <= lo) return null;
  const pad = (hi - lo) * 0.5;
  const min = Math.min(lo - pad, v);
  const max = Math.max(hi + pad, v);
  const pct = (x) => Math.max(0, Math.min(100, ((x - min) / (max - min)) * 100));
  return { marker: pct(v), bandStart: pct(lo), bandEnd: pct(hi) };
};

export const groupTestsByDepartment = (tests) => {
  const groups = [];
  tests.forEach((t) => {
    const name = t.departmentName || 'Pathology';
    let g = groups.find((x) => x.name === name);
    if (!g) { g = { name, tests: [] }; groups.push(g); }
    g.tests.push(t);
  });
  return groups;
};

export const collectAbnormal = (tests, obsMap) => {
  const out = [];
  tests.forEach((t) => {
    (obsMap[t.testId] || t.observations || []).forEach((o) => {
      const flag = o.currentFlag || 'N';
      if (isFlagged(flag)) out.push({ test: t.investigationName, name: getObsName(o), value: getObsValue(o), unit: o.unit || '', range: formatRefRange(o), kind: flagKind(flag) });
    });
  });
  return out;
};

export const fmtDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
};

export const centreAddressLine = (c) => [c?.address, c?.city, c?.state].filter(Boolean).join(', ');
export const centreContactLine = (c) => [c?.mobile ? `Ph: ${c.mobile}` : null, c?.email].filter(Boolean).join('  |  ');
export const centreInitials = (name) => String(name || 'L').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
